import { useEffect, useState } from "react";

import { AppRouter } from "./Routes/AppRouter";
import { storage } from "./Shared/Utils/authStorage";
import {
  markSessionEnded,
  onSessionExpired,
  refreshAccessToken,
} from "./Shared/Services/ApiClient";
import { NotificationProvider } from "./Shared/Context/NotificationContext";
import { AnalyticsProvider } from "./Shared/Context/AnalyticsContext";
import { ThemeProvider } from "./Shared/Context/ThemeContext";
import ThemeToggleButton from "./App/Components/ThemeToggleButton";
import WebSocketInitializer from "./App/Utils/WebSocketInitializer";
import ShareUrlRedirect from "./App/Utils/ShareUrlRedirect";
import SessionExpiredOverlay from "./App/Components/SessionExpiredOverlay";
import SeoManager from "./Shared/Seo/SeoManager";
import { fetchCurrentPrivileges } from "./Admin/Services/AdminRoleService";

function App() {
  const [authLoading, setAuthLoading] = useState(true);
  const [authReady, setAuthReady] = useState(false);
  const [sessionExpired, setSessionExpired] = useState(false);

  useEffect(() => {
    const restoreSession = async () => {
      try {
        // The shared refresh call retries transient failures and de-duplicates concurrent
        // calls, so a page load during a slow network no longer ends the session.
        const accessToken = await refreshAccessToken();

        if (!accessToken) {
          throw new Error("No access token returned");
        }

        const user = storage.getUser<{ id: string; role: string }>();

        if (
          user?.role === "SUPER_ADMIN" ||
          user?.role === "DELEGATED_ADMIN"
        ) {
          try {
            const privilegesRes = await fetchCurrentPrivileges();
            storage.setPrivileges(
              privilegesRes.success && privilegesRes.data
                ? privilegesRes.data
                : []
            );
          } catch {
            storage.setPrivileges([]);
          }
        }

        setAuthReady(true);

      } catch (error) {
        // No session to restore - a first visit, or a return after the browser dropped the
        // refresh cookie. The login screen is the right answer here, and the API layer stops
        // trying to refresh a session that does not exist.
        console.error("Refresh Failed", error);
        markSessionEnded();
        storage.clearAuth();
        setAuthReady(true);
      } finally {
        setAuthLoading(false);
      }
    };

    restoreSession();
  }, []);

  // The API layer tells us when the session is genuinely over, so the user gets an
  // explanation instead of the page silently jumping to the login screen.
  useEffect(() => {
    return onSessionExpired(() => {
      setSessionExpired(true);
    });
  }, []);

  // VERY IMPORTANT
  if (authLoading) {
    return (
      <>
      {/* The head is stamped even while the session is being restored, so a crawler and a
          person on a slow connection both see the right title and robots directive. */}
      <SeoManager />
      <div className="min-h-screen flex items-center justify-center">
        Loading...
      </div>
      </>
    );
  }

  return (
    <>
    <ThemeProvider>
    <div className="min-h-screen">
      <AnalyticsProvider>
      <NotificationProvider authReady={authReady}>
      <SeoManager />
      <WebSocketInitializer />
      <ShareUrlRedirect />
      <AppRouter />
      </NotificationProvider>
      </AnalyticsProvider>
    </div>
    {/* <ThemeToggleButton /> */}
    </ThemeProvider>

    <SessionExpiredOverlay
      open={sessionExpired}
      onSignIn={() => {
        storage.clearAuth();
        window.location.href = "/login";
      }}
    />
    </>
  );
}

export default App;
